import React, { useMemo } from 'react';
import { FileText, AlertTriangle, AlertCircle, Info, CheckCircle2 } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { useQualityWorkspaceState } from './useQualityWorkspaceState';
export function ContentQuality() {
  const {
    filteredRecords
  } = useQualityWorkspaceState();
  const rows = useMemo(() => filteredRecords.map(record => ({
    ...record,
    issues: record.issues.filter(i => i.category === 'content')
  })).filter(record => record.issues.length > 0), [filteredRecords]);
  const criticalCount = rows.reduce((sum, r) => sum + r.issues.filter(i => i.severity === 'critical').length, 0);
  const warningCount = rows.reduce((sum, r) => sum + r.issues.filter(i => i.severity === 'warning').length, 0);
  return <div className="space-y-4" data-id="element-2173">
      <Card className="p-5" data-id="element-2174">
        <div className="flex items-center justify-between mb-3" data-id="element-2175">
          <div className="flex items-center gap-2" data-id="element-2176">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-2177">
              <FileText className="w-5 h-5 text-blue-600" data-id="element-2178" />
            </div>
            <div data-id="element-2179">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-2180">
                Content Quality
              </h3>
              <p className="text-sm text-slate-500" data-id="element-2181">
                {rows.length} questions need content improvements
              </p>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-2182">
            {criticalCount > 0 && <Badge variant="danger" size="sm" data-id="element-2183">
                {criticalCount} critical
              </Badge>}
            {warningCount > 0 && <Badge variant="warning" size="sm" data-id="element-2184">
                {warningCount} warnings
              </Badge>}
            {rows.length === 0 && <Badge variant="success" size="sm" data-id="element-2185">
                All passing
              </Badge>}
          </div>
        </div>
      </Card>

      {rows.length === 0 ? <Card className="p-12 text-center" data-id="element-2186">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" data-id="element-2187" />
          <h3 className="text-lg font-bold text-slate-900" data-id="element-2188">
            No content issues
          </h3>
          <p className="text-slate-500" data-id="element-2189">
            All questions pass content quality checks in the current filter
            scope.
          </p>
        </Card> : <div className="space-y-3" data-id="element-2190">
          {rows.slice(0, 100).map(record => {
        const hasCritical = record.issues.some(i => i.severity === 'critical');
        return <Card key={record.question.id} className={`p-4 border-l-4 ${hasCritical ? 'border-l-rose-500' : 'border-l-amber-400'}`} data-id="element-2191">
                <div className="flex items-center justify-between mb-3" data-id="element-2192">
                  <code className="text-sm font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded" data-id="element-2193">
                    {record.question.id}
                  </code>
                  <div className="flex gap-1.5" data-id="element-2194">
                    <Badge variant="neutral" size="sm" data-id="element-2195">
                      {record.question.module}
                    </Badge>
                    <Badge variant="neutral" size="sm" data-id="element-2196">
                      T{record.question.tier} · G{record.question.group}
                    </Badge>
                  </div>
                </div>
                <ul className="space-y-1.5" data-id="element-2197">
                  {record.issues.map(issue => <li key={issue.id} className="flex items-start gap-2 text-sm" data-id="element-2198">
                      <SeverityIcon severity={issue.severity} data-id="element-2199" />
                      <span className={issue.severity === 'critical' ? 'text-rose-700' : issue.severity === 'warning' ? 'text-amber-700' : 'text-slate-600'} data-id="element-2200">
                        {issue.message}
                      </span>
                    </li>)}
                </ul>
              </Card>;
      })}
          {rows.length > 100 && <p className="text-center text-sm text-slate-500 py-2" data-id="element-2201">
              Showing 100 of {rows.length} — use filters to narrow results.
            </p>}
        </div>}
    </div>;
}
function SeverityIcon({
  severity
}: {
  severity: string;
}) {
  if (severity === 'critical') return <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" data-id="element-2202" />;
  if (severity === 'warning') return <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" data-id="element-2203" />;
  return <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" data-id="element-2204" />;
}